import React, { useRef, useState } from 'react';
import { Link, useLoaderData, useNavigate, useParams } from 'react-router-dom';
import {
  NJAccordion,
  NJAccordionItem,
  NJAccordionItemContent,
  NJAccordionItemHeader,
  NJButton,
  NJFormItem,
  NJIconButton,
  NJSelectItem,
  NJSelectRoot as NJSelectInput,
  NJSidebarBrandDeprecated as NJSidebarBrand,
  NJSidebarContentDeprecated as NJSidebarContent,
  NJSidebarDeprecated as NJSidebar,
  NJSidebarDividerDeprecated as NJSidebarDivider,
  NJSidebarMenuDeprecated as NJSidebarMenu,
  NJSpinner,
  NJToastService
} from '@engie-group/fluid-design-system-react';
import { useMutation } from '@tanstack/react-query';

import { formState, useForm } from '@genai/form';
import { SidebarFooter, SidebarLink } from '@genai/sidebar';

import styles from './page.module.css';
import { type Collection, type CollectionId, type CreateCollectionForm, type StoredFile } from '../model';
import { LoaderData } from '../loaders';
import { createCollection } from './createCollection';
import { editCollection } from './editCollection';
import { uploadFile } from './uploadFile';
import { listFiles } from './listFiles';
import { deleteCollection } from './deleteCollection';
import { deleteFile } from './deleteFile';
import { FileModal } from './fileModal';
import { ConfirmationModal } from '@genai/confirmation';
import { FileId } from '../../file/model';

export const CollectionPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const loaderData = useLoaderData() as LoaderData;

  const { collectionId } = useParams() as { collectionId: CollectionId };

  const [collections, setCollections] = useState<Collection[]>(
    loaderData.collections || []
  );

  const organizations = loaderData.organizations || [];

  const currentCollection = collections.filter(
    (collection) => collection.uuid === collectionId
  )[0];

  const [name, setName] = useState<string>(currentCollection?.name || '');

  const [embedModel, setEmbedModel] = useState<string>(
    currentCollection?.embedModel || ''
  );

  const [currentUserOrganization] = useState<string>(
    loaderData.user.organization || ''
  );

  const [organization, setOrganization] = useState<string>(
    currentUserOrganization || ''
  );

  const [files, setFiles] = useState<StoredFile[]>(loaderData.files || []);

  const [isFileModalOpen, setIsFileModalOpen] = useState<boolean>(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);

  const [isFileDeleteConfirmationModalOpen, setIsFileDeleteConfirmationModalOpen] = useState<boolean>(false);

  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const fileToDeleteId = useRef<FileId | null>(null);

  const duplicateCollectionForm = () => {
    setName((prevName) => prevName + ' copie');
    NJToastService('Collection dupliquée avec succès !');
    setIsDuplicate(true);
  };

  const collectionToForm = (collection: Collection | null) => {
    setName(collection?.name || '');
    setEmbedModel(collection?.embedModel || '');
    setOrganization(collection?.organization || '');
  };

  const { isPending: isListFilesPending, mutate: listCollectionFilesMutate } =
    useMutation({
      mutationFn: async (collectionId: CollectionId) => {
        return listFiles(collectionId);
      },
      onSuccess: setFiles,
      onError: (error) => {
        NJToastService(
          `Une erreur est survenue lors du chargement des fichiers : ${error.message}`
        );
      }
    });

  const {
    isPending: isUpdateFilesPending,
    mutate: updateCollectionFilesMutate
  } = useMutation({
    mutationFn: async (mutation: { file: File; chunkingStrategy: string }) => {
      const { file, chunkingStrategy } = mutation;
      await uploadFile(collectionId, file, chunkingStrategy);
      return listFiles(collectionId);
    },
    onSuccess: setFiles,
    onError: (error) => {
      NJToastService(
        `Une erreur est survenue lors du chargement des fichiers : ${error.message}`
      );
    }
  });

  const { isPending: isEditPending, mutate: editCollectionMutate } =
    useMutation({
      mutationFn: editCollection.bind(this, collectionId),
      onSuccess: () => {
        NJToastService('Collection modifiée avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const { isPending: isCreatePending, mutate: createCollectionMutate } =
    useMutation({
      mutationFn: createCollection,
      onSuccess: (newCollection) => {
        setCollections([...collections, newCollection]);
        NJToastService('Collection créée avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const { isPending: isDeletePending, mutate: deleteCollectionMutate } =
    useMutation({
      mutationFn: deleteCollection.bind(null, collectionId),
      onSuccess: () => {
        setCollections([
          ...collections.filter(
            (collection) => collection.uuid !== collectionId
          )
        ]);
        collectionToForm(null);
        setFiles([]);
        navigate('/admin/collection');
        NJToastService('Collection supprimée avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const { isPending: isFileDeletionPending, mutate: deleteFileMutate } =
    useMutation({
      mutationFn: deleteFile.bind(null, collectionId),
      onSuccess: (_, deletedFileId: FileId) => {
        setFiles([
          ...(loaderData.files?.filter((file) => file.uuid !== deletedFileId) ||
            [])
        ]);
        NJToastService('Fichier supprimé avec succès !');
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      }
    });

  const isPending = isEditPending || isCreatePending || isDeletePending;

  const { formStatus, errors, onSubmit } = useForm<CreateCollectionForm>({
    handleSubmit: !collectionId || isDuplicate ? createCollectionMutate : editCollectionMutate,
    requiredFields: {
      name: 'Nom requis',
      embed_model: 'Modèle d\'embedding requis',
      organization: 'Stratégie d\'organisation requise'
    }
  });

  return (
    <>
      <NJSidebar isFolded={false}>
        <NJSidebarBrand imageSrc="/techhelp-logo.png" />

        <NJSidebarMenu className="genai-sidebar-menu">
          <div>
            <div className="genai-block_btn_new">
              <Link
                className="genai-sidebar-new"
                to={`/admin/collection`}
              >
                <NJButton
                  className="genai-sidebar-btn-new"
                  icon="add_circle_outline"
                  label="New collection"
                  variant="secondary"
                />
              </Link>
            </div>
          </div>

          <NJSidebarDivider className="genai-sidebar-divider" />
          <div className="genai-sidebar-menu-content">
            <h4 className={styles['sidebar-content-title']}>
              Liste des collections
            </h4>
            {collections.map((collection) => (
              <SidebarLink
                key={collection.uuid}
                active={collectionId === collection.uuid}
                onClick={async () => {
                  listCollectionFilesMutate(collection.uuid);
                  collectionToForm(collection);
                }}
                to={`/admin/collection/${collection.uuid}`}
              >
                {collection.name}
              </SidebarLink>
            ))}
          </div>
        </NJSidebarMenu>

        <SidebarFooter
          onNavigate={() => {
            collectionToForm(null);
            setFiles([]);
          }}
        />
      </NJSidebar>

      <NJSidebarContent className={styles['sidebar-content']}>
        <div className={styles['sidebar-content-wrapper']}>
          {!collectionId || isDuplicate ? (
            <h1>Créer une nouvelle collection</h1>
          ) : (
            <h1>Modifier une collection</h1>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit({
                name,
                embed_model: embedModel,
                organization: organization
              });
            }}
          >
            <NJFormItem
              label="Collection name"
              id="CollectionName"
              value={name}
              isRequired
              disabled={!!collectionId && !isDuplicate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const target = event.target as HTMLInputElement;
                setName(target.value || '');
              }}
            />

            <NJSelectInput
              buttonDefaultValueLabel="Choisissez un modèle d'embedding"
              id="embed_model"
              label="Embed Model"
              isDisabled={!!collectionId}
              listNavigationLabel="Choisissez un modèle d'embedding"
              value={embedModel}
              isRequired
              onChange={(newValue) => setEmbedModel(newValue as string)}
            >
              {loaderData.embedModels ? (
                loaderData.embedModels.map((embedModel) => (
                  <NJSelectItem value={embedModel}>{embedModel}</NJSelectItem>
                ))
              ) : (
                <NJSelectItem value="text-embedding-ada-002">
                  text-embedding-ada-002
                </NJSelectItem>
              )}
            </NJSelectInput>

            <NJSelectInput
              buttonDefaultValueLabel="Choisissez une organisation"
              id="organization"
              label="Organisation"
              isDisabled={!!currentUserOrganization}
              listNavigationLabel="Choisissez une organisation"
              value={currentUserOrganization}
              isRequired
              onChange={(newValue) => setOrganization(newValue as string)}
            >
              {organizations.map((organization) => (
                <NJSelectItem key={organization.uuid} value={organization.uuid}>
                  {organization.name}
                </NJSelectItem>
              ))}
            </NJSelectInput>

            <NJAccordion>
              <NJAccordionItem>
                <NJAccordionItemHeader>
                  <div className={styles['files-header']}>
                    {(isUpdateFilesPending ||
                      isFileDeletionPending ||
                      isListFilesPending) && <NJSpinner scale="xs" aria-label="" />}
                    <p>Fichiers {`(${files.length})`}</p>
                  </div>
                </NJAccordionItemHeader>

                <NJAccordionItemContent className={styles['file-accordion']}>
                  <ConfirmationModal
                    content="Êtes-vous sûr de vouloir supprimer ce fichier ?"
                    isOpen={isFileDeleteConfirmationModalOpen}
                    onClose={(confirm) => {
                      if (confirm && fileToDeleteId.current) {
                        deleteFileMutate(fileToDeleteId.current);
                      }
                      setIsFileDeleteConfirmationModalOpen(false);
                    }}
                  />

                  {files.map((file) => (
                    <div className={styles['file']}>
                      <p>{file.name}</p>

                      <p className={styles['status']}>{file.status}</p>

                      <NJIconButton
                        icon="delete"
                        variant="tertiary"
                        size="md"
                        label="supprimer un fichier"
                        onClick={() => {
                          fileToDeleteId.current = file.uuid;
                          setIsFileDeleteConfirmationModalOpen(true);
                        }}
                      />
                    </div>
                  ))}
                </NJAccordionItemContent>
              </NJAccordionItem>
            </NJAccordion>

            <div className={styles['validation-container']}>
              <p>
                {formStatus === formState.error
                  ? errors.map((error) => (
                    <span key={crypto.randomUUID()}>
                        - {error} <br />
                      </span>
                  ))
                  : null}
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}
              >
                {isPending && <NJSpinner scale="xs" aria-label="" />}
                {collectionId && !isDuplicate && (
                  <>
                    {isFileModalOpen ? (
                      <FileModal
                        onSubmit={(file: File, chunkingStrategy: string) => {
                          if (!collectionId || !file || !chunkingStrategy)
                            return;
                          updateCollectionFilesMutate({
                            file,
                            chunkingStrategy
                          });
                          setIsFileModalOpen(false);
                        }}
                        onClose={() => {
                          setIsFileModalOpen(false);
                        }}
                      />
                    ) : null}

                    <ConfirmationModal
                      content="Êtes-vous sûr de vouloir supprimer cette collection ?"
                      isOpen={isConfirmationModalOpen}
                      onClose={(confirm) => {
                        if (confirm) {
                          deleteCollectionMutate();
                        }
                        setIsConfirmationModalOpen(false);
                      }}
                    />

                    <NJButton
                      onClick={() => {
                        duplicateCollectionForm();
                      }}
                      variant="secondary"
                      label="Dupliquer"
                    />

                    <NJButton
                      variant="destructive"
                      label="Supprimer"
                      onClick={setIsConfirmationModalOpen.bind(null, true)}
                    />

                    <NJButton
                      label="Envoyer un fichier"
                      onClick={() => setIsFileModalOpen(true)}
                    />
                  </>
                )}

                <NJButton
                  disabled={isPending}
                  type="submit"
                  label="Enregistrer la collection"
                />
              </div>
            </div>
          </form>
        </div>
      </NJSidebarContent>
    </>
  );
};
